import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, Heart, Star, Users, Calendar, Award, Play, ChevronRight,
  Check, Sparkles, Cross, Shield, Music, Palette, Clock, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const ChristianEducationLanding = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [selectedLevel, setSelectedLevel] = useState('seeds');

  const levels = [
    {
      id: 'seeds',
      name: t('christian.seeds'),
      ages: t('christian.seeds_age'),
      icon: '🌱',
      color: 'from-green-400 to-emerald-500',
      focus: ['Songs & worship', 'Bible stories', 'Basic prayers', 'Coloring activities']
    },
    {
      id: 'roots',
      name: t('christian.roots'),
      ages: t('christian.roots_age'),
      icon: '🌿',
      color: 'from-blue-400 to-indigo-500',
      focus: ['Scripture memory', 'Bible understanding', 'Character lessons', 'Obedience']
    },
    {
      id: 'warriors',
      name: t('christian.warriors'),
      ages: t('christian.warriors_age'),
      icon: '⚔️',
      color: 'from-purple-400 to-pink-500',
      focus: ['Theology basics', 'Leadership skills', 'Evangelism', 'Deep Bible study']
    }
  ];

  const features = [
    { icon: BookOpen, title: '52-Week Curriculum', desc: 'Complete Bible journey from Genesis to Revelation' },
    { icon: Heart, title: 'Character Building', desc: 'Trust, obedience, courage, and love' },
    { icon: Calendar, title: 'Weekly Structure', desc: 'Sunday church, Wednesday study, Friday prayer' },
    { icon: Award, title: 'Scripture Memory', desc: 'Scripture Ladder system with badges' },
    { icon: Music, title: 'Worship Songs', desc: 'Age-appropriate praise and worship' },
    { icon: Shield, title: 'Safe Environment', desc: 'Parent-controlled, child-friendly content' }
  ];

  const curriculumQuarters = [
    { name: 'Creation & Beginnings', weeks: '1-13', theme: 'Trust God', icon: '🌍' },
    { name: 'God Builds a Nation', weeks: '14-26', theme: 'Obedience', icon: '🏛️' },
    { name: 'Kings & Prophets', weeks: '27-39', theme: 'Courage', icon: '👑' },
    { name: 'Jesus & The Church', weeks: '40-52', theme: 'Love & Service', icon: '✝️' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Cross className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-bold text-lg">Zazu</span>
              <span className="text-purple-400 text-xs block -mt-1">Christian Education</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="compact" className="border-white/20 text-white bg-white/10 hover:bg-white/20" />
            <Link to="/christian/login">
              <Button variant="ghost" className="text-white hover:text-purple-300 hover:bg-white/10">
                {t('nav.login')}
              </Button>
            </Link>
            <Link to="/christian/register">
              <Button className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700">
                {t('christian.start_free_trial')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300">{t('christian.subtitle')}</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {t('christian.hero_title_prefix')}{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                {t('christian.hero_title_highlight')}
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              {t('christian.hero_description_full')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/christian/register">
                <Button size="lg" className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-lg px-8 py-6">
                  <Play className="w-5 h-5 mr-2" />
                  {t('christian.start_free_trial_7day')}
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20 text-lg px-8 py-6" onClick={() => document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' })}>
                {t('christian.view_curriculum')}
              </Button>
            </div>
            
            <p className="text-sm text-slate-400 mt-4">
              Only <span className="text-purple-400 font-bold">$2.99/month</span> after trial • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Levels Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-transparent to-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Age-Appropriate Learning Levels</h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Each level is carefully designed to meet children where they are spiritually and developmentally.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {levels.map((level, idx) => (
              <motion.div
                key={level.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card 
                  className={`bg-slate-800/50 border-slate-700/50 hover:border-purple-500/50 cursor-pointer transition-all ${selectedLevel === level.id ? 'ring-2 ring-purple-500' : ''}`}
                  onClick={() => setSelectedLevel(level.id)}
                >
                  <CardContent className="p-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${level.color} flex items-center justify-center text-3xl mb-4`}>
                      {level.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">Level: {level.name}</h3>
                    <p className="text-purple-400 text-sm mb-4">Ages {level.ages}</p>
                    <ul className="space-y-2">
                      {level.focus.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                          <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Everything Your Child Needs</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="bg-slate-800/30 rounded-2xl p-6 border border-slate-700/50 hover:border-purple-500/50 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-400">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section id="curriculum" className="py-16 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">52-Week Bible Journey</h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            A complete year of spiritual growth divided into four powerful quarters.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {curriculumQuarters.map((quarter, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700/50">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-3">{quarter.icon}</div>
                  <h3 className="font-bold text-white mb-1">{quarter.name}</h3>
                  <p className="text-sm text-purple-400 mb-2">Weeks {quarter.weeks}</p>
                  <div className="inline-flex items-center gap-1 bg-purple-500/20 rounded-full px-3 py-1">
                    <Heart className="w-3 h-3 text-purple-400" />
                    <span className="text-xs text-purple-300">{quarter.theme}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Scripture Memory Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-3xl p-8 border border-purple-500/30">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Scripture Ladder System</h3>
                <p className="text-purple-300">7-Day Memorization Method</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-7 gap-2 mb-8">
              {['Hear', 'Repeat', 'Write', 'Explain', 'Recite', 'Teach', 'Group'].map((day, idx) => (
                <div key={idx} className="bg-slate-800/50 rounded-xl p-3 text-center">
                  <div className="text-xs text-purple-400 mb-1">Day {idx + 1}</div>
                  <div className="text-sm font-medium text-white">{day}</div>
                </div>
              ))}
            </div>
            
            <h4 className="font-bold text-white mb-4">Badge Milestones</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3">
                <span className="text-2xl">🥉</span>
                <div>
                  <p className="text-sm font-medium text-white">Bronze</p>
                  <p className="text-xs text-slate-400">5 verses</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3">
                <span className="text-2xl">🥈</span>
                <div>
                  <p className="text-sm font-medium text-white">Silver</p>
                  <p className="text-xs text-slate-400">20 verses</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3">
                <span className="text-2xl">🥇</span>
                <div>
                  <p className="text-sm font-medium text-white">Gold</p>
                  <p className="text-xs text-slate-400">50 verses</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3">
                <span className="text-2xl">🏆</span>
                <div>
                  <p className="text-sm font-medium text-white">Champion</p>
                  <p className="text-xs text-slate-400">100 verses</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Simple, Affordable Pricing</h2>
          <p className="text-slate-400 mb-12">Start your child's faith journey today</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free Plan */}
            <Card className="bg-slate-800/30 border-slate-600/50 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-500 to-slate-600" />
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">$0</span>
                  <span className="text-slate-400">/forever</span>
                </div>
                <ul className="space-y-3 mb-8 text-left text-sm">
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-slate-400" /> First 2 weeks of curriculum
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-slate-400" /> Basic scripture memory
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-slate-400" /> Worship songs access
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-slate-400" /> 1 worksheet per week
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 line-through">
                    Push notifications
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 line-through">
                    All 52 weeks
                  </li>
                </ul>
                <Link to="/christian/register?plan=christian_free">
                  <Button variant="outline" className="w-full border-slate-600 text-white hover:bg-slate-700">
                    Get Started Free
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Christian Plan */}
            <Card className="bg-slate-800/50 border-purple-500/50 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
              <div className="absolute top-4 right-4 bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                POPULAR
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">Christian Education</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">$2.99</span>
                  <span className="text-slate-400">/month</span>
                </div>
                <ul className="space-y-3 mb-8 text-left text-sm">
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400" /> Full 52-week curriculum
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400" /> Scripture Ladder system
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400" /> All worksheets (3/week)
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400" /> Push notifications
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400" /> Badges & rewards
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400" /> Progress tracking
                  </li>
                </ul>
                <Link to="/christian/register?plan=christian">
                  <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700">
                    Start 7-Day Trial
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            {/* Christian Plus Plan */}
            <Card className="bg-gradient-to-b from-purple-900/50 to-slate-800/50 border-amber-500/50 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
              <div className="absolute top-4 right-4 bg-amber-500 text-slate-900 text-xs font-bold px-2 py-1 rounded-full">
                BEST VALUE
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">Christian Plus</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">$4.99</span>
                  <span className="text-slate-400">/month</span>
                </div>
                <ul className="space-y-3 mb-8 text-left text-sm">
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-amber-400" /> Everything in Christian
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-amber-400" /> Reading & Math basics
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-amber-400" /> AI-powered lessons
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-amber-400" /> 5 worksheets per week
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-amber-400" /> Science exploration
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <Check className="w-4 h-4 text-amber-400" /> Full curriculum access
                  </li>
                </ul>
                <Link to="/christian/register?plan=christian_plus">
                  <Button className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:from-amber-500 hover:to-orange-600">
                    Start 7-Day Trial
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl mb-6">✝️</div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            "Train up a child in the way he should go"
          </h2>
          <p className="text-slate-400 mb-8">Proverbs 22:6</p>
          <Link to="/christian/register">
            <Button size="lg" className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-lg px-8 py-6">
              Begin Your Family's Faith Journey
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Cross className="w-4 h-4 text-white" />
            </div>
            <span className="text-slate-400">Zazu Christian Education</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/help" className="hover:text-white">Help</Link>
            <Link to="/" className="hover:text-white">Main Site</Link>
          </div>
          <p className="text-sm text-slate-500">© 2026 Zazu Education</p>
        </div>
      </footer>
    </div>
  );
};

export default ChristianEducationLanding;
